import { useLanguage } from '@/context/LanguageContext';
import { translations } from '@/data/translations';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

export default function CraftPage() {
  const { t } = useLanguage();

  return (
    <div data-testid="craft-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_craft_cover.webp"
            alt="Craftsman hands working on wood"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="relative z-10 max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-3xl">
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-none mb-6">
              {t(translations.craft.title)}
            </h1>
            <p className="font-sans text-lg md:text-xl text-white/80 leading-relaxed">
              {t(translations.craft.subtitle)}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <span className="font-sans text-sm uppercase tracking-widest text-[#8B5A2B] mb-4 block">
                {t(translations.craft.storyTitle)}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] tracking-tight mb-6">
                {t(translations.craft.storyHeading)}
              </h2>
              <div className="space-y-4 font-sans text-base text-[#5D5D5D] leading-relaxed">
                <p>{t(translations.craft.storyParagraph1)}</p>
                <p>{t(translations.craft.storyParagraph2)}</p>
                <p>{t(translations.craft.storyParagraph3)}</p>
              </div>
              <div className="mt-8">
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-[#2C2C2C] text-[#2C2C2C] px-8 py-4 h-auto hover:bg-[#2C2C2C] hover:text-white transition-colors duration-300 text-sm uppercase tracking-widest"
                >
                  <a
                    href="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/katalog.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="craft-lookbook-btn"
                  >
                    {t(translations.home.lookbook)}
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden">
              <img
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_craft_helmut_santifaller.webp"
                alt="Portrait of Helmut Santifaller"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <Separator className="max-w-[1800px] mx-auto bg-[#E5E5E5]" />

      {/* Process Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="text-center mb-16">
            <span className="font-sans text-sm uppercase tracking-widest text-[#8B5A2B] mb-4 block">
              {t(translations.craft.processTitle)}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] tracking-tight">
              {t(translations.craft.processHeading)}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F9F8F6] border border-[#E5E5E5] flex items-center justify-center">
                <span className="font-serif text-2xl text-[#8B5A2B]">01</span>
              </div>
              <h3 className="font-serif text-xl text-[#2C2C2C] mb-3">
                {t(translations.craft.processStep1Title)}
              </h3>
              <p className="font-sans text-sm text-[#5D5D5D] leading-relaxed">
                {t(translations.craft.processStep1Text)}
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F9F8F6] border border-[#E5E5E5] flex items-center justify-center">
                <span className="font-serif text-2xl text-[#8B5A2B]">02</span>
              </div>
              <h3 className="font-serif text-xl text-[#2C2C2C] mb-3">
                {t(translations.craft.processStep2Title)}
              </h3>
              <p className="font-sans text-sm text-[#5D5D5D] leading-relaxed">
                {t(translations.craft.processStep2Text)}
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F9F8F6] border border-[#E5E5E5] flex items-center justify-center">
                <span className="font-serif text-2xl text-[#8B5A2B]">03</span>
              </div>
              <h3 className="font-serif text-xl text-[#2C2C2C] mb-3">
                {t(translations.craft.processStep3Title)}
              </h3>
              <p className="font-sans text-sm text-[#5D5D5D] leading-relaxed">
                {t(translations.craft.processStep3Text)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
              <div className="aspect-square rounded-sm overflow-hidden">
                <img
                  src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_craft_material_section_img1.webp"
                  alt="Wood grain detail"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square rounded-sm overflow-hidden mt-8">
                <img
                  src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_craft_material_section_img2.webp"
                  alt="Craftsmanship detail"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="font-sans text-sm uppercase tracking-widest text-[#8B5A2B] mb-4 block">
                {t(translations.craft.materialsTitle)}
              </span>
              <h2 className="font-serif text-4xl md:text-4xl text-[#2C2C2C] tracking-tight mb-6">
                {t(translations.craft.materialsHeading)}
              </h2>
              <div className="space-y-4 font-sans text-base text-[#5D5D5D] leading-relaxed">
                <p>
                  <strong className="text-[#2C2C2C]">{t(translations.craft.materialsStyle)}</strong>{' '}
                  {t(translations.craft.materialsStyleText)}
                </p>
                <p>
                  <strong className="text-[#2C2C2C]">{t(translations.craft.materialsWhy)}</strong>{' '}
                  {t(translations.craft.materialsWhyText)}
                </p>
                <p>
                  <strong className="text-[#2C2C2C]">{t(translations.craft.materialsPrimary)}</strong>{' '}
                  {t(translations.craft.materialsPrimaryText)}
                </p>
                <p>
                  <strong className="text-[#2C2C2C]">{t(translations.craft.materialsDifference)}</strong>{' '}
                  {t(translations.craft.materialsDifferenceText)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
